import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import { BenchmarkRun } from '../../../services/benchmarkService';
import { alertsToCases, kycSummary, sampleName } from './suppliedSampleCopy';
import { dsVar, ink } from '../../../theme/tokens';

/**
 * Marks a run as a supplied sample, so it can never be mistaken for the
 * institution's own benchmark.
 *
 * "Supplied sample · alerts.csv · 16 alerts → 16 cases · KYC: kyc.csv, 16 of
 * 16 customers, dated before their alerts", with the full provenance a click
 * away.
 */
const SuppliedSampleBanner: React.FC<{
    run: BenchmarkRun;
    onOpenProvenance: () => void;
}> = ({ run, onOpenProvenance }) => {
    const source = run.source;
    if (source?.dataSource !== 'user_supplied') return null;
    const kyc = kycSummary(source.kyc);
    const warnings = source.warnings?.length ?? 0;

    return (
        <Box
            role="note"
            sx={{
                mt: 3,
                p: 2,
                borderRadius: '10px',
                border: '1px solid rgba(56, 189, 248, 0.4)',
                backgroundColor: 'rgba(56, 189, 248, 0.07)',
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1.5
            }}
        >
            <Box className="flex items-start gap-1.5" sx={{ minWidth: 0 }}>
                <UploadFileOutlinedIcon sx={{ color: ink('#38BDF8'), fontSize: 20, mt: '1px' }} />
                <Typography sx={{ fontSize: '0.875rem', color: dsVar('fg-soft'), lineHeight: 1.6 }}>
                    <Box component="span" sx={{ fontWeight: 700, color: ink('#7DD3FC') }}>Supplied sample</Box>
                    {' · '}
                    <Box component="span" sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.8125rem' }}>
                        {sampleName(source.sourceName)}
                    </Box>
                    {' · '}
                    {alertsToCases(source.rowsSupplied, run.predictionCount)}
                    {' · '}
                    KYC: {kyc ?? 'none supplied, read from your customer records'}
                    {warnings > 0 && (
                        <Box component="span" sx={{ color: ink('#FBBF24') }}>
                            {' · '}{warnings} {warnings === 1 ? 'warning' : 'warnings'} when loaded
                        </Box>
                    )}
                </Typography>
            </Box>
            <Button
                onClick={onOpenProvenance}
                size="small"
                sx={{
                    textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem', color: ink('#38BDF8'),
                    border: '1px solid rgba(56, 189, 248, 0.4)', borderRadius: '6px', px: 1.5,
                    '&:hover': { backgroundColor: 'rgba(56, 189, 248, 0.1)' }
                }}
            >
                Provenance
            </Button>
        </Box>
    );
};

export default SuppliedSampleBanner;
